import { Renderer, Program, Mesh, Triangle } from 'ogl';  // yarn add ogl

export default class GradientBlinds {
  constructor(container, options = {}) {
    if (!container) return;
    this.container = container;
    
    // Gradient Blinds
    // https://www.reactbits.dev/backgrounds/gradient-blinds
    this.options = {
      gradientColors: options.gradientColors || ['#FF9FFC', '#5227FF'],
      angle: options.angle || 0,
      noise: options.noise || 0.3,
      blindCount: options.blindCount || 12,
      blindMinWidth: options.blindMinWidth || 50,
      spotlightRadius: options.spotlightRadius || 0.5,
      spotlightSoftness: options.spotlightSoftness || 1,
      spotlightOpacity: options.spotlightOpacity || 1,
      mouseDampening: options.mouseDampening || 0.15,
      distortAmount: options.distortAmount || 0,
      shineDirection: options.shineDirection || 'left',
      ...options
    };

    this.mouseTarget = [0, 0];
    this.lastTime = 0;
    this.firstResize = true;
    
    this.init();
	// console.log("WebGL 資源已初始化");
  }

  // 顏色轉換工具
  hexToRGB(hex) {
    const c = hex.replace('#', '').padEnd(6, '0');
    return [parseInt(c.slice(0, 2), 16) / 255, parseInt(c.slice(2, 4), 16) / 255, parseInt(c.slice(4, 6), 16) / 255];
  }

  init() {
    this.renderer = new Renderer({
      dpr: window.devicePixelRatio || 1,
      alpha: true,
      antialias: true
    });
    
    const gl = this.renderer.gl;
    this.canvas = gl.canvas;
    Object.assign(this.canvas.style, { width: '100%', height: '100%', display: 'block' });
    this.container.appendChild(this.canvas);

	requestAnimationFrame(() => {
		this.canvas.classList.add('is-loaded');
	});

    // Shader 程式碼 (保持不變)
    const vertex = `attribute vec2 position; attribute vec2 uv; varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position, 0.0, 1.0); }`;
    const fragment = `
      precision mediump float;
      uniform vec3 iResolution; uniform vec2 iMouse; uniform float iTime;
      uniform float uAngle; uniform float uNoise; uniform float uBlindCount;
      uniform float uSpotlightRadius; uniform float uSpotlightSoftness; uniform float uSpotlightOpacity;
      uniform float uDistort; uniform float uShineFlip;
      uniform vec3 uColor0, uColor1, uColor2, uColor3, uColor4, uColor5, uColor6, uColor7;
      uniform int uColorCount;
      varying vec2 vUv;
      float rand(vec2 co){ return fract(sin(dot(co, vec2(12.9898,78.233))) * 43758.5453); }
      vec2 rotate2D(vec2 p, float a){ float c = cos(a), s = sin(a); return mat2(c, -s, s, c) * p; }
      vec3 getGradientColor(float t){
        float tt = clamp(t, 0.0, 1.0);
        float scaled = tt * float(uColorCount - 1);
        float f = fract(scaled);
        int seg = int(floor(scaled));
        if (seg == 0) return mix(uColor0, uColor1, f); if (seg == 1) return mix(uColor1, uColor2, f);
        if (seg == 2) return mix(uColor2, uColor3, f); if (seg == 3) return mix(uColor3, uColor4, f);
        if (seg == 4) return mix(uColor4, uColor5, f); if (seg == 5) return mix(uColor5, uColor6, f);
        if (seg == 6) return mix(uColor6, uColor7, f);
        return uColor7;
      }
      void main() {
        vec2 uv0 = vUv;
        float aspect = iResolution.x / iResolution.y;
        vec2 p = uv0 * 2.0 - 1.0; p.x *= aspect;
        vec2 pr = rotate2D(p, uAngle); pr.x /= aspect;
        vec2 uv = pr * 0.5 + 0.5;
        if (uDistort > 0.0) {
          uv.x += sin(uv.y * 6.0) * (0.01 * uDistort);
          uv.y += cos(uv.x * 6.0) * (0.01 * uDistort);
        }
        vec3 base = getGradientColor(uv.x);
        vec2 offset = vec2(iMouse.x/iResolution.x, iMouse.y/iResolution.y);
        float d = length(uv0 - offset);
        float spot = (1.0 - 2.0 * pow(d/max(uSpotlightRadius,0.001), uSpotlightSoftness)) * uSpotlightOpacity;
        float stripe = fract(uv.x * uBlindCount);
        if (uShineFlip > 0.5) stripe = 1.0 - stripe;
        vec3 col = vec3(spot) + base - vec3(stripe);
        col += (rand(vUv + iTime) - 0.5) * uNoise;
        gl_FragColor = vec4(col, 1.0);
      }
    `;

    // 準備顏色
    const colors = this.options.gradientColors;
    const colorArr = colors.map(c => this.hexToRGB(c));
    while (colorArr.length < 8) colorArr.push(colorArr[colorArr.length - 1]);

    this.uniforms = {
      iResolution: { value: [0, 0, 1] },
      iMouse: { value: [0, 0] },
      iTime: { value: 0 },
      uAngle: { value: (this.options.angle * Math.PI) / 180 },
      uNoise: { value: this.options.noise },
      uBlindCount: { value: this.options.blindCount },
      uSpotlightRadius: { value: this.options.spotlightRadius },
      uSpotlightSoftness: { value: this.options.spotlightSoftness },
      uSpotlightOpacity: { value: this.options.spotlightOpacity },
      uDistort: { value: this.options.distortAmount },
      uShineFlip: { value: this.options.shineDirection === 'right' ? 1 : 0 },
      uColorCount: { value: colors.length },
      uColor0: { value: colorArr[0] }, uColor1: { value: colorArr[1] },
      uColor2: { value: colorArr[2] }, uColor3: { value: colorArr[3] },
      uColor4: { value: colorArr[4] }, uColor5: { value: colorArr[5] },
      uColor6: { value: colorArr[6] }, uColor7: { value: colorArr[7] }
    };

    this.program = new Program(gl, { vertex, fragment, uniforms: this.uniforms });
    this.mesh = new Mesh(gl, { geometry: new Triangle(gl), program: this.program });

    // 事件監聽
    this.onPointerMove = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      this.mouseTarget = [(e.clientX - rect.left) * this.renderer.dpr, (rect.height - (e.clientY - rect.top)) * this.renderer.dpr];
    };
    window.addEventListener('pointermove', this.onPointerMove);

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this.container);

    this.resize();
    this.animate();
  }

  resize() {
    const rect = this.container.getBoundingClientRect();
    this.renderer.setSize(rect.width, rect.height);
    this.uniforms.iResolution.value = [this.renderer.gl.drawingBufferWidth, this.renderer.gl.drawingBufferHeight, 1];
    
    if (this.firstResize) {
      this.firstResize = false;
      this.mouseTarget = [this.renderer.gl.drawingBufferWidth / 2, this.renderer.gl.drawingBufferHeight / 2];
    }
  }

  animate(t) {
    this.raf = requestAnimationFrame((t) => this.animate(t));
    this.uniforms.iTime.value = t * 0.001;

    const factor = 1 - Math.exp(-(t - this.lastTime) / 1000 / Math.max(1e-4, this.options.mouseDampening));
    this.lastTime = t;
    
    const cur = this.uniforms.iMouse.value;
    cur[0] += (this.mouseTarget[0] - cur[0]) * (factor || 1);
    cur[1] += (this.mouseTarget[1] - cur[1]) * (factor || 1);

    this.renderer.render({ scene: this.mesh });
  }

  // 銷毀方法 (避免記憶體洩漏)
  destroy() {
	// 1. 停止動畫循環 (防止繼續運算)
	if (this.raf) {
		cancelAnimationFrame(this.raf);
	}
	// 2. 停止監測容器大小
	if (this.ro) {
		this.ro.disconnect();
	}
	// 3. 移除滑鼠監聽
	window.removeEventListener('pointermove', this.onPointerMove);
	// 4. 從 DOM 中移除畫布
	if (this.canvas && this.canvas.parentElement) {
		this.container.removeChild(this.canvas);
	}
	// 5. 正確釋放 WebGL 資源
	const gl = this.renderer.gl;
	const extension = gl.getExtension('WEBGL_lose_context');
	if (extension) {
		extension.loseContext();
	}
	// console.log("WebGL 資源已釋放，Context 已強制中斷");
  }
}